import { firebaseDate } from "@poseidon/utils/yupSchemaTypes";
import { boolean, InferType, number, object, ObjectSchema, string } from "yup";
import {
  AffordabilitySection,
  CurrentMonthlyCommitmentBlock,
  IncomeBlock,
  MonthlyLivingCostsBlock,
  NewBoatCostsBlock,
  Subset,
} from "./pages/creditUnderwriting/affordabilityState";

export const creditUnderwritingSchema = object({
  creditReportStatus: string().default(() => "pending"),
  creditSummaryStatus: string().default(() => "pending"),
  creditSummaryStatusExpiry: firebaseDate.nullable(),
  updatedAt: firebaseDate,
});

const metaObjectDefinition = object({
  applicationIsJoint: boolean().defined(),
}).default(() => ({
  applicationIsJoint: false,
}));

type MetaObject = InferType<typeof metaObjectDefinition>;

const jointApplicationProperty = number().when("meta", {
  is: (val: MetaObject) => {
    return val?.applicationIsJoint === true;
  },
  then: (schema) => schema.defined(),
  otherwise: (schema) => schema.optional(),
});

const incomeSchema = (
  { optional } = { optional: false }
): ObjectSchema<IncomeBlock> | ObjectSchema<Subset<IncomeBlock>> => {
  const schema = optional ? number().optional() : number().defined();
  return object({
    mainApplicantIncome: schema,
    jointApplicantIncome: jointApplicationProperty,
    mainApplicantOtherIncome: schema,
    jointApplicantOtherIncome: jointApplicationProperty,
  });
};

const currentMonthlyCommitmentSchema = (
  { optional } = { optional: false }
):
  | ObjectSchema<CurrentMonthlyCommitmentBlock>
  | ObjectSchema<Subset<CurrentMonthlyCommitmentBlock>> => {
  const schema = optional ? number().optional() : number().defined();
  return object({
    mortgageRent: schema,
    totalLoans: schema,
    outstandingCreditCardBalances: schema,
    outstandingBNPLBalances: schema,
    otherCommittedExpenses: schema,
  });
};

const monthlyLivingCostsSchema = (
  { optional } = { optional: false }
):
  | ObjectSchema<MonthlyLivingCostsBlock>
  | ObjectSchema<Subset<MonthlyLivingCostsBlock>> => {
  const schema = optional ? number().optional() : number().defined();
  return object({
    numberOfDependants: schema,
    onsGeneratedLivingCosts: schema,
  });
};

const newBoatCostsSchema = (
  { optional } = { optional: false }
):
  | ObjectSchema<NewBoatCostsBlock>
  | ObjectSchema<Subset<NewBoatCostsBlock>> => {
  const schema = optional
    ? number().nullable().optional()
    : number().nullable().defined();
  return object({
    monthlyLoanPayment: schema,
    monthlyRunningCosts: schema,
  });
};

const affordabilitySectionSchema: ObjectSchema<AffordabilitySection> = object({
  income: incomeSchema() as ObjectSchema<IncomeBlock>,
  currentMonthlyCommitments:
    currentMonthlyCommitmentSchema() as ObjectSchema<CurrentMonthlyCommitmentBlock>,
  monthlyLivingCosts:
    monthlyLivingCostsSchema() as ObjectSchema<MonthlyLivingCostsBlock>,
  newBoatCosts: newBoatCostsSchema() as ObjectSchema<NewBoatCostsBlock>,
  isAffordable: boolean()
    .defined()
    .default(() => false),
  totals: object({
    income: number().default(0),
    currentMonthlyCommitments: number().default(0),
    monthlyLivingCosts: number().default(0),
    newBoatCosts: number().default(0),
  }).required(),
  workings: object({
    totalExpenditure: number().default(0),
    inflationBuffer: number().default(0),
    totalAssessedExpenditure: number().default(0),
    netSurplus: number().default(0),
    affordabilityBuffer: number().default(0),
    assessedNetSurplus: number().default(0),
  }).required(),
});

const nullableAffordabilitySectionSchema: ObjectSchema<
  Subset<AffordabilitySection>
> = object({
  income: incomeSchema({ optional: true }).optional().default({}),
  currentMonthlyCommitments: currentMonthlyCommitmentSchema({
    optional: true,
  })
    .optional()
    .default({}),
  monthlyLivingCosts: monthlyLivingCostsSchema({ optional: true })
    .optional()
    .default({}),
  newBoatCosts: newBoatCostsSchema({ optional: true }).optional().default({
    monthlyLoanPayment: null,
    monthlyRunningCosts: null,
  }),
  isAffordable: boolean()
    .optional()
    .default(() => false),
  totals: object({
    income: number(),
    currentMonthlyCommitments: number(),
    monthlyLivingCosts: number(),
    newBoatCosts: number().nullable(),
  }),
  workings: object({
    totalExpenditure: number(),
    inflationBuffer: number(),
    totalAssessedExpenditure: number(),
    netSurplus: number(),
    affordabilityBuffer: number(),
    assessedNetSurplus: number(),
  }),
});

export const affordabilityReportSchema = object({
  declared: affordabilitySectionSchema,
  variance: nullableAffordabilitySectionSchema,
  openBanking: nullableAffordabilitySectionSchema,
  manual: nullableAffordabilitySectionSchema,
  meta: metaObjectDefinition,
});
